import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_BOOKING = { room_id:'', guest_name:'', phone:'', check_in:'', check_out:'', notes:'' };
const STATUS_COLORS = {
  booked:'bg-blue-100 text-blue-700',
  checked_in:'bg-green-100 text-green-700',
  checked_out:'bg-gray-100 text-gray-500',
  cancelled:'bg-red-100 text-red-600'
};

export default function GuestRooms() {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [guestHouses, setGuestHouses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRoomMgmt, setShowRoomMgmt] = useState(false);
  const [form, setForm] = useState(EMPTY_BOOKING);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterGH, setFilterGH] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','manager'].includes(user?.role);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get(`/api/rooms/bookings${filterStatus ? `?status=${filterStatus}` : ''}`); setBookings(r.data); }
    catch (err) { console.error('bookings:', err); setBookings([]); }
    try { const r = await api.get('/api/rooms'); setRooms(r.data); }
    catch (err) { console.error('rooms:', err); setRooms([]); }
    try { const r = await api.get('/api/guest-houses'); setGuestHouses(r.data); }
    catch (err) { console.error('guest-houses:', err); setGuestHouses([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, filterStatus]);

  // Rooms filtered by selected guest house
  const filteredRooms = filterGH
    ? rooms.filter(r => String(r.guest_house_id) === String(filterGH))
    : rooms;

  // Active rooms only for room cards
  const activeRooms = filteredRooms.filter(r => r.is_active !== false);

  const roomStatus = activeRooms.map(r => {
    const active = bookings.find(b => b.room_id === r.id && ['booked','checked_in'].includes(b.status));
    return { ...r, currentBooking: active };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/api/rooms/bookings/${editing}`, form);
      else await api.post('/api/rooms/bookings', form);
      setShowForm(false); setForm(EMPTY_BOOKING); setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (b) => {
    setForm({
      room_id: b.room_id, guest_name: b.guest_name, phone: b.phone||'',
      check_in: b.check_in?.split('T')[0]||b.check_in,
      check_out: b.check_out?.split('T')[0]||b.check_out||'',
      notes: b.notes||''
    });
    setEditing(b.id); setShowForm(true);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/api/rooms/bookings/${id}`, { ...bookings.find(b => b.id === id), status });
      load();
    } catch { alert('Status update failed'); }
  };

  // Room management
  const handleRoomToggle = async (room) => {
    try {
      await api.put(`/api/rooms/${room.id}`, { ...room, is_active: !room.is_active });
      load();
    } catch (err) { alert('Update failed'); }
  };

  const handleRoomDelete = async (room) => {
    if (!window.confirm(`Delete room "${room.room_name}"? This will fail if it has active bookings.`)) return;
    try {
      await api.delete(`/api/rooms/${room.id}`);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  };

  const calcNights = () => {
    if (!form.check_in || !form.check_out) return 0;
    return Math.max(0, Math.ceil((new Date(form.check_out) - new Date(form.check_in)) / 86400000));
  };

  // Group rooms by guest house for management view
  const roomsByGH = guestHouses.map(gh => ({
    ...gh,
    rooms: rooms.filter(r => r.guest_house_id === gh.id)
  }));
  const unlinkedRooms = rooms.filter(r => !r.guest_house_id);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Guest Room Booking</h1>
          <p className="text-gray-500 text-sm">{activeRooms.length} rooms available</p>
        </div>
        <div className="flex gap-2">
          {isManager && (
            <button onClick={() => setShowRoomMgmt(true)}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              🛏️ Manage Rooms
            </button>
          )}
          <button onClick={() => { setForm(EMPTY_BOOKING); setEditing(null); setShowForm(true); }}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ New Booking
          </button>
        </div>
      </div>

      {/* Guest House filter tabs */}
      {guestHouses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilterGH('')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${!filterGH ? 'bg-blue-700 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>
            All Houses
          </button>
          {guestHouses.map(gh => (
            <button key={gh.id} onClick={() => setFilterGH(gh.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${String(filterGH) === String(gh.id) ? 'bg-blue-700 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              🏠 {gh.name}
            </button>
          ))}
        </div>
      )}

      {/* Room Grid */}
      {roomStatus.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
          <p className="text-4xl mb-2">🛏️</p>
          <p>No active rooms found. Add rooms in Guest Houses section.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roomStatus.map(room => (
            <div key={room.id} className={`rounded-xl border-2 p-4 ${
              room.currentBooking ? 'border-orange-300 bg-orange-50' : 'border-green-300 bg-green-50'
            }`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-800">{room.room_name}</h3>
                  <p className="text-xs text-gray-500">{room.floor || ''}</p>
                  {room.guest_house_name && (
                    <p className="text-xs text-blue-600 mt-0.5">🏠 {room.guest_house_name}</p>
                  )}
                  <p className="text-xs text-gray-400 capitalize">{room.room_type}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  room.currentBooking ? 'bg-orange-200 text-orange-700' : 'bg-green-200 text-green-700'
                }`}>
                  {room.currentBooking ? 'Occupied' : 'Free'}
                </span>
              </div>
              {room.currentBooking && (
                <div className="mt-2 text-xs text-gray-600">
                  <p className="font-medium">{room.currentBooking.guest_name}</p>
                  <p>In: {new Date(room.currentBooking.check_in).toLocaleDateString()}</p>
                </div>
              )}
              {room.amenities && <p className="text-xs text-gray-400 mt-2">{room.amenities}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Status Filter */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex gap-2 flex-wrap">
        {['','booked','checked_in','checked_out','cancelled'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === s ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {s ? s.replace('_',' ') : 'All'}
          </button>
        ))}
      </div>

      {/* Booking Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{editing ? 'Edit Booking' : 'New Booking'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Room *</label>
                <select value={form.room_id}
                  onChange={e => setForm({...form, room_id: e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Room —</option>
                  {/* Group by guest house */}
                  {roomsByGH.map(gh => gh.rooms.length > 0 && (
                    <optgroup key={gh.id} label={`🏠 ${gh.name}`}>
                      {gh.rooms.filter(r => r.is_active !== false).map(r => (
                        <option key={r.id} value={r.id}>
                          {r.room_name} ({r.room_type}{r.floor ? `, ${r.floor}` : ''})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  {unlinkedRooms.length > 0 && (
                    <optgroup label="Other Rooms">
                      {unlinkedRooms.filter(r => r.is_active !== false).map(r => (
                        <option key={r.id} value={r.id}>
                          {r.room_name} ({r.floor || r.room_type})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
                  <input type="text" value={form.guest_name}
                    onChange={e => setForm({...form, guest_name: e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="text" value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-In *</label>
                  <input type="date" value={form.check_in}
                    onChange={e => setForm({...form, check_in: e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-Out</label>
                  <input type="date" value={form.check_out}
                    onChange={e => setForm({...form, check_out: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              {calcNights() > 0 && (
                <p className="text-sm text-blue-600 font-medium">🌙 {calcNights()} night(s)</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Create Booking'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Room Management Modal */}
      {showRoomMgmt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold">Manage Rooms</h2>
              <button onClick={() => setShowRoomMgmt(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="p-6 space-y-5">
              {/* Rooms by Guest House */}
              {roomsByGH.map(gh => gh.rooms.length > 0 && (
                <div key={gh.id}>
                  <h3 className="font-semibold text-gray-700 mb-2">🏠 {gh.name}
                    <span className="text-xs text-gray-400 ml-2 font-normal">{gh.city || ''}</span>
                  </h3>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Room</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Cap</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Floor</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {gh.rooms.map(r => {
                          const hasActiveBooking = bookings.some(b => b.room_id === r.id && ['booked','checked_in'].includes(b.status));
                          return (
                            <tr key={r.id} className={!r.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                              <td className="px-3 py-2 font-medium">{r.room_name}</td>
                              <td className="px-3 py-2 capitalize">{r.room_type}</td>
                              <td className="px-3 py-2">{r.capacity}</td>
                              <td className="px-3 py-2">{r.floor || '—'}</td>
                              <td className="px-3 py-2">
                                {hasActiveBooking ? (
                                  <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs">Occupied</span>
                                ) : r.is_active ? (
                                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">Available</span>
                                ) : (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">Disabled</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex gap-1">
                                  <button onClick={() => handleRoomToggle(r)}
                                    className={`text-xs px-2 py-1 border rounded ${
                                      r.is_active ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-green-50 hover:text-green-600'
                                    }`}>
                                    {r.is_active ? '🚫 Disable' : '✅ Enable'}
                                  </button>
                                  {isAdmin && !hasActiveBooking && (
                                    <button onClick={() => handleRoomDelete(r)}
                                      className="text-xs px-2 py-1 border rounded hover:bg-red-50 hover:text-red-600">
                                      🗑️ Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Unlinked / Sample rooms */}
              {unlinkedRooms.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">📦 Other Rooms (not linked to a guest house)</h3>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Room</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Floor</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {unlinkedRooms.map(r => {
                          const hasActiveBooking = bookings.some(b => b.room_id === r.id && ['booked','checked_in'].includes(b.status));
                          return (
                            <tr key={r.id} className={!r.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                              <td className="px-3 py-2 font-medium">{r.room_name}</td>
                              <td className="px-3 py-2 capitalize">{r.room_type}</td>
                              <td className="px-3 py-2">{r.floor || '—'}</td>
                              <td className="px-3 py-2">
                                {hasActiveBooking ? (
                                  <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs">Occupied</span>
                                ) : r.is_active ? (
                                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">Available</span>
                                ) : (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">Disabled</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex gap-1">
                                  <button onClick={() => handleRoomToggle(r)}
                                    className={`text-xs px-2 py-1 border rounded ${
                                      r.is_active ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-green-50 hover:text-green-600'
                                    }`}>
                                    {r.is_active ? '🚫 Disable' : '✅ Enable'}
                                  </button>
                                  {isAdmin && !hasActiveBooking && (
                                    <button onClick={() => handleRoomDelete(r)}
                                      className="text-xs px-2 py-1 border rounded hover:bg-red-50 hover:text-red-600">
                                      🗑️ Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {rooms.length === 0 && (
                <p className="text-center text-gray-400 py-8">No rooms found. Add rooms in Guest Houses section.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bookings Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b"><h2 className="font-semibold text-gray-700">All Bookings</h2></div>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : bookings.length === 0 ? (
          <div className="p-12 text-center text-gray-400"><div className="text-5xl mb-3">🏨</div><p>No bookings</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Room','Guest House','Guest','Phone','Check-In','Check-Out','Nights','Status',''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bookings.map(b => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{b.room_name}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{b.guest_house_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-800">{b.guest_name}</td>
                    <td className="px-4 py-3 text-gray-600">{b.phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{new Date(b.check_in).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-gray-600">{b.check_out ? new Date(b.check_out).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.nights}</td>
                    <td className="px-4 py-3">
                      <select value={b.status} onChange={e => handleStatusChange(b.id, e.target.value)}
                        className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${STATUS_COLORS[b.status]}`}>
                        {['booked','checked_in','checked_out','cancelled'].map(s => (
                          <option key={s} value={s}>{s.replace('_',' ')}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleEdit(b)} className="p-1 hover:bg-gray-100 rounded">✏️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
